/**
 * THE API ERROR CONTRACT — one error model for the whole frontend.
 *
 * ============================================================================
 * THIS FILE IS NOT A MOCK, AND THAT IS WHY IT IS NOT IN mock.ts.
 *
 * `src/api/mock.ts` is headed "MOCK/TEMPORARY" and every line of it is deleted the
 * day the modules in this directory perform real requests. The error contract has the
 * opposite lifetime: it is what a real `fetch` wrapper will construct, what stores
 * will branch on, and what components will render. Leaving `ApiError` inside the
 * temporary file meant the durable half of the boundary was scheduled for deletion
 * alongside the fixtures.
 *
 * So the class moved here and `mock.ts` re-exports it. Nothing that imports
 * `ApiError` from `./mock` breaks, and nothing new should: import from here.
 * ============================================================================
 *
 * WHY A CONTRACT AND NOT A CONVENTION. Before this, each consumer decided for itself
 * what a failure was. supportStore checked `err instanceof ApiError` and otherwise
 * substituted its own sentence; dnaStore and mixerStore stored a bare string; the
 * optimistic writes in songsStore and playlistsStore discarded rejections entirely
 * (`void songsApi.x()`). Three shapes and one silence for one idea. A backend that
 * starts returning 401s would have to be handled at each of them separately, and the
 * fourth would never handle it at all.
 *
 * WHAT IS HERE
 * ------------
 *   ApiError            the thrown value: a status, a message, and an optional code
 *   ApiErrorKind        the branch a caller actually wants — see below
 *   toApiError          normalises ANY caught value into an ApiError
 *   apiErrorKind        maps a status onto the branch
 *   isUnauthorized etc. the three predicates worth naming
 *
 * WHY `kind` AND NOT JUST `status`. Callers do not care about 402 versus 409; they
 * care that the answer was "you have to pay for this", because that is the branch that
 * opens the upgrade prompt. Reading a status at the call site means every call site
 * has to know the mapping, and they will not agree — docs/api-contract.md §2 already
 * fixes which statuses mean what, and this is that table as code.
 *
 * WHAT IS DELIBERATELY NOT HERE
 * -----------------------------
 * No toast. No navigation. No retry. An error contract that reached for the UI store
 * would put UI behaviour inside the API layer, which is the boundary Batch 7 §27 draws
 * ("no UI behaviour in API services"). Deciding what a 401 looks like on screen is the
 * hook's job; this only says that it was a 401.
 *
 * See docs/api-contract.md §2 for the wire format these map to, and §21 of the Batch 7
 * brief for the requirement.
 */

/**
 * Machine-readable failure codes a backend may send alongside the status.
 *
 * OPEN-ENDED ON PURPOSE — the type is `ApiErrorCode | string`, not a closed union, at
 * the one place it is consumed. A backend will grow codes faster than this file is
 * edited, and a closed union would make an unrecognised code a compile error in the
 * frontend rather than an unrecognised code. The named members are the ones the UI
 * actually branches on today.
 */
export type ApiErrorCode =
  | 'unauthorized'
  | 'session_expired'
  | 'forbidden'
  | 'not_found'
  | 'validation_failed'
  | 'payment_required'
  | 'insufficient_credits'
  | 'rate_limited'
  | 'unsupported_capability'
  | 'server_error'
  | 'network_error';

/**
 * The branch a caller wants, derived from the status.
 *
 * One value per *reaction*, not one per status code:
 *
 *   auth          the session is gone or was never valid. Send them to /login.
 *   forbidden     the user is signed in, but this operation is not allowed.
 *   payment       the account is not entitled to this. Offer the upgrade.
 *   validation    the request itself was wrong. Show it inline on the field.
 *   notFound      the thing addressed does not exist. Usually a stale link.
 *   unsupported   the capability does not exist in this build. Explain, do not retry.
 *   server        the backend broke. Retry is reasonable.
 *   network       the request never arrived. Retry is reasonable.
 *   unknown       anything else, including a non-Error throw.
 */
export type ApiErrorKind =
  | 'auth'
  | 'forbidden'
  | 'payment'
  | 'validation'
  | 'notFound'
  | 'unsupported'
  | 'server'
  | 'network'
  | 'unknown';

/**
 * The plain-object form of a failure — what crosses the wire and what a store may
 * hold in state.
 *
 * A STORE SHOULD HOLD THIS, NOT THE `ApiError` INSTANCE. Zustand state is compared,
 * serialised in devtools and snapshotted in tests; an Error carries a stack that
 * differs on every throw, so holding the instance makes equality checks meaningless
 * and snapshots unstable. `toApiErrorShape` produces this.
 *
 * Named `ApiErrorShape` rather than `APIError` because `ApiError` — the class — is
 * already the name of the thrown value, and two exports differing only in case is a
 * mistake waiting to be imported. Batch 7 §23 asks for an `APIError` type; this is it.
 */
export interface ApiErrorShape {
  status: number;
  message: string;
  code?: ApiErrorCode | string;
  kind: ApiErrorKind;
}

/**
 * The thrown value for every failed API call.
 *
 * `status` mirrors the HTTP status the real endpoint is expected to return, so a mock
 * that rejects with 422 and a backend that responds 422 are indistinguishable to the
 * caller — which is the property that lets error handling be written once, now,
 * against fixtures.
 *
 * `status: 0` is reserved for "the request never happened" (offline, DNS, CORS): there
 * is no HTTP status because there was no HTTP response.
 */
export class ApiError extends Error {
  /** Mirrors the HTTP status the real endpoint is expected to return. 0 = no response. */
  readonly status: number;

  /** Machine-readable code, when the backend sends one. */
  readonly code?: ApiErrorCode | string;

  constructor(status: number, message: string, code?: ApiErrorCode | string) {
    super(message);
    // Set explicitly rather than relying on the class name: every build in this repo
    // targets ES2020+ where `extends Error` preserves the prototype, but a downlevel
    // target silently sets it to 'Error' and `err.name` is used in log output.
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  /** The branch a caller should take. See ApiErrorKind. */
  get kind(): ApiErrorKind {
    return apiErrorKind(this.status, this.code);
  }
}

/**
 * Maps a status (and code, when present) onto the branch a caller reacts to.
 *
 * The status table is docs/api-contract.md §2's, which is the one the backend is being
 * written against. The code is consulted only where a status is ambiguous: 403 is
 * "forbidden", which is an entitlement problem when the backend says so
 * (`payment_required`) and a plain refusal otherwise.
 */
export function apiErrorKind(status: number, code?: ApiErrorCode | string): ApiErrorKind {
  if (code === 'unsupported_capability') return 'unsupported';
  if (code === 'payment_required' || code === 'insufficient_credits') return 'payment';

  if (status === 0) return 'network';
  if (status === 401) return 'auth';
  // 402 Payment Required and 409 Conflict are both "buy something first" in this API —
  // §2 pairs them deliberately, because a purchase that is already owned and a purchase
  // that has not happened lead to the same screen.
  if (status === 402 || status === 409) return 'payment';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 422) return 'validation';
  if (status === 429) return 'server';
  if (status === 501) return 'unsupported';
  if (status >= 500) return 'server';
  if (status >= 400) return 'validation';
  return 'unknown';
}

/**
 * The message shown when a thrown value carries nothing usable.
 *
 * Exported so the sentence a user reads and the sentence a test asserts are the same
 * string. Deliberately does not name a cause: this is the branch where the cause is
 * unknown, and guessing one ("check your connection") is how an entitlement failure
 * comes to be reported as a network problem.
 */
export const UNKNOWN_API_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Normalises anything a `catch` can receive into an `ApiError`.
 *
 * WHY THIS EXISTS. `catch (err)` types `err` as `unknown`, and what actually arrives is
 * an `ApiError` from a mock, a `TypeError` from a failed `fetch`, a
 * `GenerationUnavailableError` / `UnsupportedCapabilityError` / `DistributionUnavailableError`
 * from one of this app's own capability boundaries, or — from a badly behaved library — a
 * string. Every consumer that wants to say something useful had to handle all four. Now
 * they call this.
 *
 * THE CAPABILITY ERRORS ARE MAPPED, NOT SWALLOWED. This app's honest-refusal classes
 * (see audioEditApi, generationApi, publisherApi, consentApi) are the frontend's own way
 * of saying "the backend cannot do this", which is exactly 501 Not Implemented. Mapping
 * them onto `unsupported` means a component can render a capability refusal and a
 * backend's 501 the same way, because they are the same fact — and their own carefully
 * written `message` is preserved rather than replaced by a generic one.
 *
 * `preserveMessage: false` replaces the message with `UNKNOWN_API_ERROR_MESSAGE` for
 * anything that is not already an `ApiError`. Use it where the caught value could be a
 * runtime bug: a `TypeError: undefined is not a function` shown to a user is a leak of
 * internals, not an error message.
 */
export function toApiError(err: unknown, options?: { preserveMessage?: boolean }): ApiError {
  if (err instanceof ApiError) return err;

  const preserve = options?.preserveMessage ?? true;

  if (err instanceof Error) {
    /*
      Transport failures are classified at the transport boundary (`api/http.ts`), where a
      real `fetch` rejection is actually observed. By the time a random Error reaches here,
      its constructor no longer proves what failed: a component bug, a parser bug and a
      genuine network outage can all be TypeErrors. Guessing "network" from the class name
      here is how a broken ticket-detail render becomes "check your connection".

      So this function only maps the app's own named error classes specially. Everything
      else is treated as an ordinary application failure and keeps its message when the
      caller asked to preserve it.
    */

    /*
      This app's own error classes, matched by `name` rather than by `instanceof`.

      NAMES RATHER THAN IMPORTS, deliberately: importing audioEditApi, generationApi,
      publisherApi, consentApi, audioUploadApi, lyria and labelgrid here would make the
      error contract depend on seven feature modules, several of which already import
      this file — a cycle. Every one of these classes declares `readonly name = '…'` as a
      class field (not a constructor assignment), so the name survives minification and
      is present on the instance. The tests for this file construct each class and assert
      the mapping, so a rename cannot silently fall through to 'unknown'.

      TWO GROUPS, AND THE SPLIT IS THE POINT. They are not all the same kind of failure,
      and collapsing them would lose the distinction the classes were created to draw.
    */

    /*
      GROUP 1 — "this build has no backend for that". A missing capability, which is what
      501 Not Implemented means. Nothing is broken, retrying will not help, and the
      message names what is absent.
    */
    const CAPABILITY_ERRORS = [
      // audioEditApi — trim, tempo, pitch, mix, master, export, and the region edits
      'UnsupportedCapabilityError',
      // generationApi — Image Music, which needs Lyria 3 and a server-held credential
      'GenerationUnavailableError',
      // publisherApi — distribution, which needs LabelGrid
      'DistributionUnavailableError',
      // audioUploadApi — there is nowhere to store an uploaded file
      'AudioUploadUnavailableError',
      // consentApi — there is no consent store to file the record in
      'ConsentUnavailableError',
    ];
    if (CAPABILITY_ERRORS.includes(err.name)) {
      // The original message is kept regardless of `preserveMessage`: these messages are
      // the whole reason those classes exist. They are written for the person reading the
      // screen and they name the missing capability. Replacing them with "Something went
      // wrong" would turn a disclosed limitation back into a mystery failure.
      return new ApiError(501, err.message, 'unsupported_capability');
    }

    /*
      GROUP 2 — "the request itself was not acceptable". A rule was broken, and the
      caller can fix it and try again. That is 422, not 501: the capability exists (or
      would), and what failed was this particular request.

      SubmissionRefusedError is here rather than in group 1 even though one of its
      reasons is `no-entitlement`, because the class's own contract is "a precondition of
      the flow is unmet" and it carries a machine-readable `reason` for the UI to route
      on. Mapping the whole class to 'payment' would be wrong for the other four reasons,
      and mapping it to 'unsupported' would tell the user nothing is possible when in
      fact they need to finish a step. Callers that need the distinction read `.reason`
      on the class directly — which is what it is for — and this mapping only decides
      what a generic handler does with it.
    */
    const REQUEST_ERRORS = [
      // lyria.buildLyriaRequest — too many images, empty prompt, unsupported MIME type
      'LyriaRequestError',
      // labelgrid.buildReleaseRequest — a required release field is missing
      'LabelGridRequestError',
      // publisherApi.submitRelease — a step of the release flow has not been completed
      'SubmissionRefusedError',
    ];
    if (REQUEST_ERRORS.includes(err.name)) {
      // Message kept for the same reason: these are written by whoever knew the rule.
      return new ApiError(422, err.message, 'validation_failed');
    }

    return new ApiError(
      500,
      preserve && err.message ? err.message : UNKNOWN_API_ERROR_MESSAGE,
      'server_error'
    );
  }

  // A thrown string, number, object or undefined. Nothing here is a message worth
  // showing, so the status is 500 and the text is the generic one either way.
  return new ApiError(500, UNKNOWN_API_ERROR_MESSAGE, 'server_error');
}

/**
 * The plain-object form, for storing in state. See ApiErrorShape.
 *
 * `code` is omitted rather than set to `undefined` when absent, so two shapes for the
 * same failure are deep-equal — which is what makes them comparable in a test.
 */
export function toApiErrorShape(err: unknown, options?: { preserveMessage?: boolean }): ApiErrorShape {
  const apiError = toApiError(err, options);
  return {
    status: apiError.status,
    message: apiError.message,
    kind: apiError.kind,
    ...(apiError.code === undefined ? {} : { code: apiError.code }),
  };
}

/**
 * The message to show for a caught value.
 *
 * The one-liner most call sites want: `showToast(apiErrorMessage(err))`. Equivalent to
 * `toApiError(err).message`, and named so that the intent at the call site is "get the
 * text" rather than "construct an error and reach into it".
 */
export function apiErrorMessage(err: unknown, options?: { preserveMessage?: boolean }): string {
  return toApiError(err, options).message;
}

/**
 * True when the failure means the session is gone — the caller should send the user to
 * sign in again.
 *
 * A named predicate rather than `err.status === 401` at every call site, because the
 * transport and the session gate both want the same answer and the mapping lives here.
 */
export function isUnauthorized(err: unknown): boolean {
  return toApiError(err).kind === 'auth';
}

/** True when the failure means "buy or upgrade something first". */
export function isPaymentRequired(err: unknown): boolean {
  return toApiError(err).kind === 'payment';
}

/**
 * True when the failure means the capability does not exist in this build.
 *
 * The branch that must NOT be retried and must NOT be reported as a fault: nothing is
 * broken, the feature has no backend. Every consumer of this app's honest-refusal
 * classes wants this test rather than a list of ten class names.
 */
export function isUnsupported(err: unknown): boolean {
  return toApiError(err).kind === 'unsupported';
}
